import * as React from 'react';
import { Field } from '../api';
import { getInputType, isAmountField } from './seachpage-helpers';
import { DateInput } from './DateInput';
import { AmountInput } from './AmountInput';

export type ValueControlProps = {
  field?: Field;
  name: string;
  drawerId: number | null;
  getCrit: (name: string) => { operator: string; value: string } | undefined;
  handleChange: (field: string, type: 'operator'|'value', val: string) => void;
  handleInputBlur: (field: string, value: string) => void;
  handleInputKeyDown: (field: string, value: string, e: React.KeyboardEvent<HTMLInputElement>) => void;
  getMRU: (drawer: number | null, field: string) => string[];
};

export const ValueControl: React.FC<ValueControlProps> = ({ field, name, drawerId, getCrit, handleChange, handleInputBlur, handleInputKeyDown, getMRU }) => {
  if (!name) return null as any;
  const crit = getCrit(name) ?? { operator: 'eq', value: '' };
  const op = (crit as any).operator as string;
  if (op === 'empty' || op === 'notempty') return null as any;
  const inputType = field ? getInputType(field) : 'text';
  // Currency datatype maps to getInputType 'text', so check isAmountField too.
  const isNumericInput = !!field && (inputType === 'number' || isAmountField(field));
  const amountDecimals = field && isAmountField(field) ? 2 : undefined;

  if (op === 'between') {
    const parts = (crit.value || '').split('~');
    const a = parts[0] ?? '';
    const b = parts[1] ?? '';
    if (inputType === 'date') {
      return (
        <div className="value-stack">
          <DateInput value={a} placeholder="From" className="input-default" onChange={v => handleChange(name, 'value', `${v}~${b}`)} />
          <DateInput value={b} placeholder="To" className="input-default" onChange={v => handleChange(name, 'value', `${a}~${v}`)} />
        </div>
      );
    }
    if (isNumericInput) {
      return (
        <div className="value-stack">
          <AmountInput value={a} placeholder="From" className="input-default" decimals={amountDecimals} onChange={v => handleChange(name, 'value', `${v}~${b}`)} />
          <AmountInput value={b} placeholder="To" className="input-default" decimals={amountDecimals} onChange={v => handleChange(name, 'value', `${a}~${v}`)} />
        </div>
      );
    }
    return (
      <div className="value-stack">
        <input
          type={inputType === 'checkbox' ? 'text' : inputType}
          value={a}
          placeholder="From"
          onChange={e => handleChange(name, 'value', `${e.target.value}~${b}`)}
          onBlur={e => handleInputBlur(name, e.target.value)}
          onKeyDown={e => handleInputKeyDown(name, (e.target as HTMLInputElement).value, e as any)}
          className="input-default"
        />
        <input
          type={inputType === 'checkbox' ? 'text' : inputType}
          value={b}
          placeholder="To"
          onChange={e => handleChange(name, 'value', `${a}~${e.target.value}`)}
          onBlur={e => handleInputBlur(name, e.target.value)}
          onKeyDown={e => handleInputKeyDown(name, (e.target as HTMLInputElement).value, e as any)}
          className="input-default"
        />
      </div>
    );
  }

  if (inputType === 'checkbox') {
    return (
      <input
        type="checkbox"
        checked={getCrit(name)?.value === 'true'}
        onChange={e => handleChange(name, 'value', e.target.checked ? 'true' : 'false')}
        className="checkbox-inline"
      />
    );
  }

  if (inputType === 'date') {
    return (
      <DateInput
        value={getCrit(name)?.value || ''}
        className="input-default"
        onChange={v => handleChange(name, 'value', v)}
      />
    );
  }

  if (isNumericInput) {
    return (
      <AmountInput
        value={getCrit(name)?.value || ''}
        className="input-default"
        decimals={amountDecimals}
        onChange={v => handleChange(name, 'value', v)}
      />
    );
  }

  return (
    <>
      <input
        type={inputType}
        value={getCrit(name)?.value || ''}
        onChange={e => handleChange(name, 'value', e.target.value)}
        onBlur={e => handleInputBlur(name, e.target.value)}
        onKeyDown={e => handleInputKeyDown(name, (e.target as HTMLInputElement).value, e)}
        className="input-default"
        list={`mru-${drawerId}-${name}`}
        autoComplete="off"
      />
      <datalist id={`mru-${drawerId}-${name}`} className="mru-datalist">
        {getMRU(drawerId, name).map((m: string, i: number) => <option key={i} value={m} />)}
      </datalist>
    </>
  );
};

export default ValueControl;
